import { useState, useEffect, useCallback } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { clp as fmt } from "../utils/format";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const authFetch = url =>
  fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const C = {
  green:  "#059669", greenBg:  "#ECFDF5", greenBorder: "rgba(5,150,105,0.18)",
  red:    "#DC2626", redBg:    "#FEF2F2", redBorder:   "rgba(220,38,38,0.18)",
  amber:  "#D97706", amberBg:  "#FFFBEB", amberBorder: "rgba(217,119,6,0.18)",
  indigo: "#4338CA", indigoBg: "#EEF2FF", indigoBorder:"rgba(67,56,202,0.18)",
  lbl:    "#6B7280",
  border: "#E5E7EB",
  mono:   "'JetBrains Mono', 'Courier New', monospace",
};

const CATEGORIA = {
  entrada: { label: "Entrada",    color: C.green,  bg: C.greenBg,  border: C.greenBorder },
  salida:  { label: "Salida",     color: C.red,    bg: C.redBg,    border: C.redBorder   },
  retiro:  { label: "Retiro",     color: C.amber,  bg: C.amberBg,  border: C.amberBorder },
  wallet:  { label: "Herramienta",color: C.indigo, bg: C.indigoBg, border: C.indigoBorder},
};

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent, tooltip }) {
  const [hov,     setHov]     = useState(false);
  const [showTip, setShowTip] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: "1 1 clamp(140px, 45%, 180px)",
        background: "#fff",
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${accent}`,
        borderRadius: 12,
        padding: "20px 22px",
        boxShadow: hov ? "0 4px 20px rgba(0,0,0,0.07)" : "none",
        transition: "box-shadow 0.2s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: C.lbl, textTransform: "uppercase",
          letterSpacing: "0.06em", fontWeight: 600 }}>
          {label}
        </div>
        {tooltip && (
          <div style={{ position: "relative" }}>
            <span
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 15, height: 15, borderRadius: "50%",
                background: "#E5E7EB", color: "#6B7280",
                fontSize: 9, fontWeight: 700, cursor: "help",
                flexShrink: 0,
              }}
            >
              ?
            </span>
            {showTip && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 8px)", left: "50%",
                transform: "translateX(-50%)",
                background: "#111827", color: "#fff",
                fontSize: 11, lineHeight: 1.55,
                padding: "8px 12px", borderRadius: 8,
                width: 240, zIndex: 100,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                pointerEvents: "none",
              }}>
                {tooltip}
                {/* flecha */}
                <div style={{
                  position: "absolute", top: "100%", left: "50%",
                  transform: "translateX(-50%)",
                  width: 0, height: 0,
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: "5px solid #111827",
                }} />
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ fontFamily: C.mono, fontSize: 24, fontWeight: 700,
        color: "#111827", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.lbl, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── Cabecera de columna ordenable ─────────────────────────────────────────────
function ColHeader({ label, col, sort, onSort, align = "left" }) {
  const active = sort.col === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        padding: "11px 14px",
        fontSize: 11, fontWeight: 600,
        color: active ? C.indigo : C.lbl,
        textTransform: "uppercase", letterSpacing: "0.06em",
        textAlign: align, cursor: "pointer", userSelect: "none",
        whiteSpace: "nowrap",
        borderBottom: `2px solid ${active ? C.indigo : C.border}`,
        background: "#F9FAFB",
      }}
    >
      {label}{" "}
      <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>
        {active ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}
      </span>
    </th>
  );
}

// ── Sección reconciliación con Pulso ─────────────────────────────────────────
function ReconciliacionPulso({ kpis }) {
  const [abierto, setAbierto] = useState(true);

  const filas = [
    {
      label:      "Entradas",
      movimiento: kpis.total_entradas,
      pedido:     kpis.entradas_fecha_pedido ?? 0,
      color:      C.green,
      notaMovimiento: "Ganancia neta acreditada por Dropi · por fecha del movimiento",
      notaPedido:     "Ganancia neta cruzada con fecha del pedido original",
    },
    {
      label:      "Salidas (fletes fallidos)",
      movimiento: kpis.total_salidas,
      pedido:     kpis.salidas_fecha_pedido ?? 0,
      color:      C.red,
      notaMovimiento: "Cobros de devolución · fecha en que Dropi descontó",
      notaPedido:     "Cobros de devolución cruzados con fecha del pedido original",
    },
  ];

  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      {/* Header colapsable */}
      <button
        onClick={() => setAbierto(v => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", background: "none", border: "none", cursor: "pointer",
          borderBottom: abierto ? `1px solid ${C.border}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
            ¿Cuándo ocurrió el movimiento vs cuándo se hizo el pedido?
          </span>
          <span style={{ fontSize: 11, color: C.lbl, fontWeight: 500,
            background: "#F3F4F6", padding: "2px 10px", borderRadius: 20 }}>
            fecha del movimiento en finanzas vs fecha del pedido original
          </span>
        </div>
        <span style={{ fontSize: 14, color: C.lbl }}>{abierto ? "▲" : "▼"}</span>
      </button>

      {abierto && (
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Tabla comparativa */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left" }}>Concepto</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>
                    Por fecha de movimiento
                    <div style={{ fontWeight: 400, color: C.lbl, fontSize: 10, marginTop: 2 }}>
                      coincide con el filtro de Wallet
                    </div>
                  </th>
                  <th style={{ ...thStyle, textAlign: "right" }}>
                    Por fecha de pedido
                    <div style={{ fontWeight: 400, color: C.lbl, fontSize: 10, marginTop: 2 }}>
                      coincide con el período de Pulso
                    </div>
                  </th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => {
                  const diff = f.pedido - f.movimiento;
                  const igual = Math.abs(diff) < 1;
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: 600, color: f.color, fontSize: 13 }}>{f.label}</div>
                        <div style={{ fontSize: 11, color: C.lbl, marginTop: 2 }}>{f.notaMovimiento}</div>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: C.mono,
                        fontSize: 14, fontWeight: 700, color: f.color, whiteSpace: "nowrap" }}>
                        {fmt(f.movimiento)}
                        <div style={{ fontSize: 10, color: C.lbl, fontFamily: "inherit",
                          fontWeight: 400, marginTop: 2 }}>{f.notaMovimiento.split("·")[0]}</div>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: C.mono,
                        fontSize: 14, fontWeight: 700, color: f.color, whiteSpace: "nowrap" }}>
                        {fmt(f.pedido)}
                        <div style={{ fontSize: 10, color: C.lbl, fontFamily: "inherit",
                          fontWeight: 400, marginTop: 2 }}>{f.notaPedido.split("·")[0]}</div>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                        {igual ? (
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.green,
                            background: C.greenBg, padding: "3px 10px", borderRadius: 20 }}>
                            ✓ Coinciden
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, fontFamily: C.mono, fontWeight: 700,
                            color: diff > 0 ? C.green : C.red }}>
                            {diff > 0 ? "+" : ""}{fmt(Math.abs(diff))}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Nota explicativa */}
          <div style={{ background: "#F9FAFB", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "10px 14px", fontSize: 12, color: "#4B5563", lineHeight: 1.6 }}>
            <strong style={{ color: "#111827" }}>¿Por qué pueden diferir?</strong>{" "}
            Pulso filtra pedidos por <em>fecha del pedido</em> y fletes fallidos por{" "}
            <em>fecha del movimiento en finanzas</em>. Si un pedido se hizo en marzo pero el cobro
            de devolución aparece en abril, cada criterio lo cuenta en un período distinto.
            Para cuadrar, seleccioná el mismo rango de fechas en ambas pestañas.
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: "10px 14px", fontSize: 11, fontWeight: 600,
  color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em",
  background: "#F9FAFB", borderBottom: "2px solid #E5E7EB", whiteSpace: "nowrap",
};

// ── Componente principal ──────────────────────────────────────────────────────
export default function BilleteraDropi({ desde, hasta }) {
  const isMobile = useIsMobile();
  const [datos,    setDatos]    = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error,    setError]    = useState(null);
  const [sort,     setSort]     = useState({ col: "fecha", dir: "desc" });
  const [filtro,   setFiltro]   = useState("all");
  const [busqueda, setBusqueda] = useState("");
  const [sinFiltroFecha, setSinFiltroFecha] = useState(false);

  const fetchDatos = useCallback(async (d, h, todoElHistorial = false) => {
    setCargando(true);
    setError(null);
    try {
      const params = todoElHistorial ? "" : `?desde=${d}&hasta=${h}`;
      const res = await authFetch(`${API_URL}/api/billetera${params}`);
      if (res.ok) {
        setDatos(await res.json());
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || `Error ${res.status}`);
      }
    } catch (e) {
      console.error("[Billetera] fetch error:", e);
      setError(`No se pudo conectar al backend (${e?.message || e})`);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    fetchDatos(desde, hasta, sinFiltroFecha);
  }, [desde, hasta, sinFiltroFecha, fetchDatos]);

  const handleSort = col => {
    setSort(s => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
  };

  // ── Estados de carga / error ─────────────────────────────────────────────────
  if (cargando && !datos) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10,
      color: C.lbl, fontSize: 13, padding: "4rem 0" }}>
      <div style={{ width: 18, height: 18, border: `2px solid ${C.border}`,
        borderTop: `2px solid ${C.green}`, borderRadius: "50%",
        animation: "spin 0.8s linear infinite" }} />
      Cargando Wallet Dropi...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.2)",
      borderRadius: 12, padding: "1.5rem 2rem", maxWidth: 480 }}>
      <div style={{ fontWeight: 700, color: C.red, marginBottom: 6 }}>
        No se pudieron cargar los movimientos
      </div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 14 }}>{error}</div>
      <button
        onClick={() => fetchDatos(desde, hasta, sinFiltroFecha)}
        style={{ fontSize: 13, padding: "7px 18px", borderRadius: 8,
          border: "none", background: C.red, color: "#fff", cursor: "pointer" }}>
        Reintentar
      </button>
    </div>
  );

  if (!datos) return null;

  const { kpis, movimientos } = datos;

  // ── Filtrar + buscar ─────────────────────────────────────────────────────────
  let filas = movimientos.filter(m => {
    if (filtro !== "all" && m.categoria !== filtro) return false;
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      return (
        m.descripcion.toLowerCase().includes(q) ||
        m.orden_id.toLowerCase().includes(q) ||
        m.fecha.includes(q)
      );
    }
    return true;
  });

  // Ordenar
  filas = [...filas].sort((a, b) => {
    let va = a[sort.col] ?? "";
    let vb = b[sort.col] ?? "";
    if (sort.col === "monto") { va = Number(va); vb = Number(vb); }
    else { va = String(va); vb = String(vb); }
    if (va < vb) return sort.dir === "asc" ? -1 : 1;
    if (va > vb) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const FILTROS = [
    { key: "all",     label: "Todos"       },
    { key: "entrada", label: "Entradas"    },
    { key: "salida",  label: "Salidas"     },
    { key: "retiro",  label: "Retiros"     },
    { key: "wallet",  label: "Herramientas"},
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Título + toggle historial completo ───────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.03em" }}>
            Wallet Dropi 2.0
          </h2>
          <p style={{ margin: "3px 0 0", fontSize: 13, color: C.lbl }}>
            {sinFiltroFecha
              ? `Historial completo · ${movimientos.length} movimientos`
              : `${desde} → ${hasta} · ${movimientos.length} movimientos`}
          </p>
        </div>
        <button
          onClick={() => setSinFiltroFecha(v => !v)}
          style={{
            fontSize: 12, padding: "7px 16px", borderRadius: 20, cursor: "pointer",
            border: `1.5px solid ${sinFiltroFecha ? C.green : C.border}`,
            background: sinFiltroFecha ? C.greenBg : "#fff",
            color: sinFiltroFecha ? C.green : C.lbl,
            fontWeight: sinFiltroFecha ? 700 : 400, transition: "all 0.15s",
          }}
        >
          {sinFiltroFecha ? "✓ Ver todo el historial" : "Ver todo el historial"}
        </button>
      </div>

      {/* ── KPI cards ── */}
      <div style={{ display: "flex", gap: "clamp(10px, 2vw, 14px)", flexWrap: "wrap" }}>
        <KpiCard
          label="Total entradas"
          value={fmt(kpis.entradas_fecha_pedido ?? 0)}
          sub="Ganancia neta · por fecha del pedido original"
          accent={C.green}
          tooltip="Este monto puede no coincidir con lo realmente vendido: hay órdenes entregadas que Dropi aún no abonó a la billetera."
        />
        <KpiCard
          label="Total salidas"
          value={fmt(kpis.salidas_fecha_pedido ?? 0)}
          sub="Fletes fallidos · por fecha del pedido original"
          accent={C.red}
        />
        <KpiCard
          label="Total retiros"
          value={fmt(kpis.total_retiros)}
          sub="Retiros de saldo solicitados"
          accent={C.amber}
        />
        <KpiCard
          label="Transferencias entre Wallet"
          value={fmt(kpis.total_wallet)}
          sub="Movimientos tipo Herramienta"
          accent={C.indigo}
        />
      </div>

      {/* ── Reconciliación con Pulso ── */}
      <ReconciliacionPulso kpis={kpis} />

      {/* ── Tabla de movimientos ── */}
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>

        {/* Barra: filtros + búsqueda */}
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`,
          display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
          justifyContent: "space-between", background: "#FAFAFA" }}>

          {/* Filtro por tipo */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {cargando && (
              <div style={{ width: 13, height: 13, border: `1.5px solid ${C.border}`,
                borderTop: `1.5px solid ${C.green}`, borderRadius: "50%",
                animation: "spin 0.8s linear infinite", marginRight: 4 }} />
            )}
            {FILTROS.map(o => {
              const on = filtro === o.key;
              const cat = CATEGORIA[o.key];
              return (
                <button key={o.key} onClick={() => setFiltro(o.key)} style={{
                  fontSize: 12, fontWeight: on ? 700 : 400,
                  padding: "5px 13px", borderRadius: 20,
                  border: `1.5px solid ${on ? (cat?.color ?? C.green) : C.border}`,
                  background: on ? (cat?.bg ?? C.greenBg) : "#fff",
                  color: on ? (cat?.color ?? C.green) : C.lbl,
                  cursor: "pointer", transition: "all 0.15s",
                }}>
                  {o.label}
                </button>
              );
            })}
          </div>

          {/* Búsqueda */}
          <input
            type="text"
            placeholder="Buscar por descripción, orden o fecha..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{
              fontSize: 12, padding: "7px 12px", borderRadius: 8,
              border: `1px solid ${C.border}`, outline: "none",
              background: "#fff", color: "#111827",
              width: isMobile ? "100%" : 270,
            }}
          />
        </div>

        {/* Tabla */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <ColHeader label="Fecha"       col="fecha"       sort={sort} onSort={handleSort} />
                <ColHeader label="Tipo"        col="categoria"   sort={sort} onSort={handleSort} />
                <ColHeader label="Descripción" col="descripcion" sort={sort} onSort={handleSort} />
                <ColHeader label="Orden ID"    col="orden_id"    sort={sort} onSort={handleSort} />
                <ColHeader label="Monto"       col="monto"       sort={sort} onSort={handleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: "2.5rem", textAlign: "center", color: C.lbl, fontSize: 13 }}>
                    {movimientos.length === 0
                      ? "No hay movimientos en el período seleccionado. Probá con \"Ver todo el historial\"."
                      : "No hay movimientos para los filtros seleccionados."}
                  </td>
                </tr>
              ) : (
                filas.map((m, i) => {
                  const cat = CATEGORIA[m.categoria] || CATEGORIA.salida;
                  const isEntrada = m.categoria === "entrada";
                  return (
                    <tr
                      key={i}
                      style={{
                        borderTop: `1px solid ${C.border}`,
                        background: i % 2 === 0 ? "#fff" : "#FAFAFA",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = "#F0FDF4")}
                      onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#FAFAFA")}
                    >
                      {/* Fecha */}
                      <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: C.mono, fontSize: 12, color: "#4B5563" }}>
                          {m.fecha || "—"}
                        </span>
                      </td>

                      {/* Badge tipo */}
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
                          background: cat.bg, color: cat.color, border: `1px solid ${cat.border}`,
                          display: "inline-block", whiteSpace: "nowrap",
                        }}>
                          {cat.label}
                        </span>
                      </td>

                      {/* Descripción */}
                      <td style={{ padding: "10px 14px", maxWidth: 300 }}>
                        <span style={{ fontSize: 12, color: "#374151",
                          display: "block", overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={m.descripcion}>
                          {m.descripcion || "—"}
                        </span>
                      </td>

                      {/* Orden ID */}
                      <td style={{ padding: "10px 14px" }}>
                        {m.orden_id && m.orden_id !== "nan" && m.orden_id !== "" ? (
                          <span style={{ fontFamily: C.mono, fontSize: 11, color: C.indigo,
                            background: C.indigoBg, padding: "2px 7px", borderRadius: 6 }}>
                            {m.orden_id}
                          </span>
                        ) : (
                          <span style={{ color: "#D1D5DB" }}>—</span>
                        )}
                      </td>

                      {/* Monto */}
                      <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: C.mono, fontSize: 13, fontWeight: 700,
                          color: isEntrada ? C.green : C.red }}>
                          {isEntrada ? "+" : "−"}{fmt(Math.abs(m.monto))}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {filas.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}`, padding: "10px 16px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#FAFAFA", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 11, color: C.lbl }}>
              {filas.length} de {movimientos.length} movimientos
            </span>
            <div style={{ display: "flex", gap: 16 }}>
              {(() => {
                const sumEnt = filas.filter(m => m.categoria === "entrada").reduce((s, m) => s + m.monto, 0);
                const sumSal = filas.filter(m => m.categoria !== "entrada").reduce((s, m) => s + Math.abs(m.monto), 0);
                return (
                  <>
                    {sumEnt > 0 && (
                      <span style={{ fontFamily: C.mono, fontSize: 12, color: C.green, fontWeight: 700 }}>
                        +{fmt(sumEnt)} entradas
                      </span>
                    )}
                    {sumSal > 0 && (
                      <span style={{ fontFamily: C.mono, fontSize: 12, color: C.red, fontWeight: 700 }}>
                        −{fmt(sumSal)} salidas
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
